import {
  HttpClient,
  HttpContext,
  HttpErrorResponse,
  HttpInterceptorFn,
  provideHttpClient,
  withFetch,
  withInterceptors,
} from '@angular/common/http';
import {
  ApplicationConfig,
  inject,
  Injector,
  isDevMode,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import { provideNativeDateAdapter } from '@angular/material/core';
import {
  NavigationError,
  provideRouter,
  Router,
  withNavigationErrorHandler,
} from '@angular/router';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalService,
} from '@azure/msal-angular';
import {
  AuthenticationResult,
  BrowserCacheLocation,
  InteractionType,
  IPublicClientApplication,
  PublicClientApplication,
  SilentRequest,
} from '@azure/msal-browser';
import {
  APP_VERSION,
  AppConfigService,
  authExpiredInterceptor,
  AuthService,
  correlationIdInterceptor,
  errorInterceptor,
  PERMISSION_SERVICE,
  SKIP_ERROR_SNACKBAR,
  TranslocoHttpLoader,
} from '@ccfx/shared-lib';
import { EXTRA_USER_MENU_ITEMS_FACTORY } from '@ccfx/shared-lib/layout';
import { provideTransloco, translocoConfig } from '@jsverse/transloco';
import { catchError, firstValueFrom, from, switchMap, throwError } from 'rxjs';
import packageJson from '../../package.json';
import { routes } from './app.routes';
import { OnboardingStatusService } from './features/onboarding/services/onboarding-status.service';
import { PermissionService } from './features/shared/services/permissions.service';
import { payloadValueLimitInterceptor } from './interceptors/payload-value-limit.interceptor';
import { pendingStatusInterceptor } from './interceptors/pending-status.interceptor';

/**
 * Attaches a Bearer token to every request that targets the configured API base URL.
 *
 * This replaces the class-based MsalInterceptor to avoid a bootstrap timing bug:
 * MsalInterceptor caches its protectedResourceMap at construction time via a factory.
 * If Angular constructs MsalInterceptor before provideAppInitializer finishes loading
 * app-config.json, the factory catch-block returns an empty protectedResourceMap and
 * the interceptor silently skips token attachment for the entire session.
 *
 * By calling acquireTokenSilent directly here on each request we eliminate the cached
 * state entirely — the config and active account are always read at request time.
 */
export const msalInterceptorFn: HttpInterceptorFn = (req, next) => {
  const configService = inject(AppConfigService);

  // Skip when config hasn't loaded yet or the request is fetching the config itself
  if (!configService.isLoaded || req.url.includes('/assets/')) {
    return next(req);
  }

  let config;
  try {
    config = configService.getConfig();
  } catch {
    return next(req);
  }

  // Only attach tokens to requests targeting the configured API — not external URLs
  const apiBaseUrl = config.apiBaseUrl;
  const shouldAttachToken = apiBaseUrl
    ? req.url.startsWith(apiBaseUrl)
    : req.url.startsWith('/api');

  if (!shouldAttachToken) {
    return next(req);
  }

  const msalInstance = inject(MSAL_INSTANCE) as IPublicClientApplication;
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0] ?? null;

  // Skip token attachment only when there is genuinely no active MSAL session.
  // In mock/dev mode the user may still have a real B2C account (e.g. local dev
  // pointing at a real API), so we must not skip unconditionally when enableMocks is true.
  if (!account) {
    return next(req);
  }

  const silentRequest: SilentRequest = { scopes: config.auth.scopes, account };

  return from(msalInstance.acquireTokenSilent(silentRequest)).pipe(
    switchMap((result) => {
      if (!result.accessToken) {
        return next(req);
      }

      const authReq = req.clone({
        setHeaders: { Authorization: `Bearer ${result.accessToken}` },
      });
      return next(authReq);
    }),
    catchError((error) => {
      // If the error is an HTTP response (e.g. 400, 500), the token was attached
      // successfully but the API rejected the request — propagate the error as-is.
      if (error instanceof HttpErrorResponse) {
        return throwError(() => error);
      }
      // Silent acquisition failed — pass through without token so auth-expired-interceptor
      // can attempt a forceRefresh and redirect to login if the session is truly gone
      return next(req);
    }),
  );
};

export async function acquireBootstrapAccessToken(
  configService: AppConfigService,
  msalInstance: IPublicClientApplication,
): Promise<AuthenticationResult | null> {
  const config = configService.getConfig();

  if (config.enableMocks) {
    return null;
  }

  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0] ?? null;

  if (!account) {
    return null;
  }

  try {
    const result = await msalInstance.acquireTokenSilent({
      scopes: config.auth.scopes,
      account,
    });
    return result.accessToken ? result : null;
  } catch {
    return null;
  }
}

/**
 * SAFE FACTORIES
 * These prevent crashes during bootstrap when the config hasn't loaded yet.
 */
export function MSALInstanceFactory(configService: AppConfigService): PublicClientApplication {
  try {
    const settings = configService.getConfig();
    return new PublicClientApplication({
      auth: {
        clientId: settings.auth.clientId,
        authority: settings.auth.authority,
        redirectUri: settings.auth.redirectUri,
        postLogoutRedirectUri: settings.auth.postLogoutRedirectUri,
      },
      cache: { cacheLocation: BrowserCacheLocation.LocalStorage },
    });
  } catch {
    return new PublicClientApplication({
      auth: { clientId: '', authority: 'https://login.microsoftonline.com/common' },
    });
  }
}

export function MSALGuardConfigFactory(configService: AppConfigService): MsalGuardConfiguration {
  try {
    const settings = configService.getConfig();
    const signinPolicy = (settings as any)?.auth?.signinPolicy;
    const extraQueryParameters = signinPolicy ? { p: signinPolicy } : undefined;
    return {
      interactionType: InteractionType.Redirect,
      authRequest: { scopes: settings.auth.scopes, extraQueryParameters },
    };
  } catch {
    return { interactionType: InteractionType.Redirect, authRequest: { scopes: [] } };
  }
}

function isChunkLoadError(error: unknown): boolean {
  const msg = error instanceof Error ? error.message : String(error ?? '');
  return (
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Loading chunk') ||
    msg.includes('ChunkLoadError')
  );
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideRouter(
      routes,
      withNavigationErrorHandler((error: NavigationError) => {
        if (isChunkLoadError(error.error)) {
          window.location.assign(window.location.href);
        }
      }),
    ),
    provideNativeDateAdapter(),
    provideAppInitializer(() => {
      const configService = inject(AppConfigService);
      const injector = inject(Injector);
      const http = inject(HttpClient);
      // Load config FIRST, then create AuthService and initialize MSAL
      return firstValueFrom(configService.load()).then(() => {
        const authService = injector.get(AuthService);
        return authService.initialize().then(async () => {
          // Only proceed when the user has an active session — calling whoami without
          // a valid token would produce a 401 before the interceptor can refresh it.
          if (!authService.isAuthenticated) return;

          // Pre-warm the MSAL token cache so the MSAL interceptor can attach the Bearer
          // token to the first whoami request without falling into the 401→retry cycle.
          const hasValidToken = await authService.verifySilentAuth();
          if (!hasValidToken) return;

          // After auth init, call whoami to fetch companyId, then fetch partner status.
          // apiBaseUrl is empty in local dev — the proxy resolves /api/... relative URLs.
          // If the backend is unreachable the catch block below handles it gracefully.
          try {
            const apiBaseUrl = configService.apiBaseUrl;
            const silentContext = new HttpContext().set(SKIP_ERROR_SNACKBAR, true);
            const whoami = await firstValueFrom(
              http.get<{
                status: string;
                companyStatus: string | null;
                companyId: number | null;
                isCorporateAdmin?: boolean;
                canTrade?: boolean;
                canSign?: boolean;
                rateTierId?: number | null;
                rateTierName?: string | null;
                firstName?: string | null;
                kycReReviewDate?: string | null;
              }>(`${apiBaseUrl}/api/auth/whoami`, { context: silentContext }),
            );
            if (whoami?.companyId) {
              const onboardingStatusSvc = injector.get(OnboardingStatusService);
              onboardingStatusSvc.setCompanyId(whoami.companyId);
              if (whoami.isCorporateAdmin != null) {
                onboardingStatusSvc.setIsCorporateAdmin(whoami.isCorporateAdmin);
              }
              onboardingStatusSvc.setCanTrade(whoami.canTrade ?? false);
              onboardingStatusSvc.setCanSign(whoami.canSign ?? false);
              onboardingStatusSvc.setRateTierId(whoami.rateTierId ?? null);
              onboardingStatusSvc.setRateTierName(whoami.rateTierName ?? null);
              if (whoami.firstName) {
                onboardingStatusSvc.setFirstName(whoami.firstName);
              }
              onboardingStatusSvc.setKycReReviewDate(whoami.kycReReviewDate ?? null);

              // Store the individual user status so the UI can gate content on it.
              if (whoami.status) {
                onboardingStatusSvc.setUserStatus(whoami.status);
              }

              // Use company status from whoami — this is the authoritative company-level
              // status and avoids the need for a separate partner endpoint call that could
              // be blocked by PendingStatusMiddleware for new users.
              if (whoami.companyStatus) {
                onboardingStatusSvc.setStatus(whoami.companyStatus as any);
              }

              // Fetch company-level permissions only when the user is Active.
              // A Pending user cannot access these endpoints and the call would fail.
              if (whoami.status === 'Active') {
                try {
                  const companyPermsResponse = await firstValueFrom(
                    http.get<{
                      companyId: number;
                      permissions: { permissionKey: string; isEnabled: boolean }[];
                    }>(`${apiBaseUrl}/api/company/permissions`, {
                      context: silentContext,
                    }),
                  );
                  const enabledKeys = (companyPermsResponse?.permissions ?? [])
                    .filter((p) => p.isEnabled)
                    .map((p) => p.permissionKey);
                  onboardingStatusSvc.setEnabledCompanyPermissions(enabledKeys);
                } catch {
                  // Non-critical — no company permissions means no order types enabled
                }
              }

              // For any user whose individual account is not yet Active, check if they already
              // have a personal KYC submission on file. This covers: non-admin users waiting for
              // company or individual approval, and corporate admins whose company is PendingKYC
              // and who must complete personal KYC right after company onboarding. If a submission
              // exists the user onboarding wizard is suppressed and the portal/waiting screen shown.
              // NOTE: a 'Draft' status means the user started but has not submitted — the wizard
              // must still be shown so they can continue. Only a non-Draft response suppresses it.
              if (whoami.status && whoami.status !== 'Active') {
                try {
                  const userApp = await firstValueFrom(
                    http.get<{ status: string }>(`${apiBaseUrl}/api/user-onboarding/current`, {
                      context: silentContext,
                    }),
                  );
                  if (userApp?.status && userApp.status !== 'Draft') {
                    onboardingStatusSvc.setHasPersonalKycSubmission(true);
                  }
                } catch {
                  // 404 means no submission yet — wizard will be shown when company is Active
                }
              }
            }
          } catch (err) {
            console.warn('Status initialisation failed; status will remain as-is:', err);
          }
        });
      });
    }),
    provideHttpClient(
      withFetch(),
      withInterceptors([
        payloadValueLimitInterceptor,
        pendingStatusInterceptor,
        correlationIdInterceptor,
        msalInterceptorFn,
        authExpiredInterceptor,
        errorInterceptor,
      ]),
    ),
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
      deps: [AppConfigService],
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
      deps: [AppConfigService],
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService, // Essential: Fixes NG0201 Error
    { provide: PERMISSION_SERVICE, useExisting: PermissionService },
    {
      provide: EXTRA_USER_MENU_ITEMS_FACTORY,
      useFactory: (status: OnboardingStatusService, router: Router) => () => {
        if (!status.isCorporateAdmin()) return [];
        return [
          {
            label: 'User Management',
            icon: 'group',
            action: () => router.navigate(['/user-management']),
          },
        ];
      },
      deps: [OnboardingStatusService, Router],
    },
    { provide: APP_VERSION, useValue: packageJson.version },
    provideTransloco({
      config: translocoConfig({
        availableLangs: ['en', 'fr'],
        defaultLang: 'en',
        fallbackLang: 'en',
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
        missingHandler: {
          // In production (when prodMode is true), logMissingKey is false and Transloco's default
          // behavior for missing translation keys applies (e.g., returning the key or a fallback).
          logMissingKey: isDevMode(),
        },
      }),
      loader: TranslocoHttpLoader,
    }),
  ],
};
